/* eslint-disable no-param-reassign */
import moment from 'moment';
import { Cells } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { updateApproachReference } from 'pages/ValuationsAllocation/util';
import { parseDatabaseValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import {
  MATURITY_ALIAS,
  OPM_BACKSOLVE_DATE_ALIAS,
  VOLATILITY_SOURCE_ALIAS,
  VOLATILITY_SOURCE_REFERENCE,
} from './constants';
import { HandleVolatilitySourceProps, ReverseParserProps } from './types';
import { APPROACH_GPC_PROPERTY } from '../../../util/constants';

const handleVolatilitySource = ({ cell, calcValue }: HandleVolatilitySourceProps) => {
  if (cell.alias === VOLATILITY_SOURCE_ALIAS && Number(cell.value) === 0) {
    // If the volatility source is Specified, we need to set the cell value to null
    return null;
  }

  // Otherwise, keep the id of the selected GPC approach
  return calcValue;
};

const handleDateCells = (cell: ICell, cells: Cells) => {
  if ([OPM_BACKSOLVE_DATE_ALIAS, MATURITY_ALIAS].includes(cell.alias!)) {
    const backsolveDateCell = cells.B1;
    const maturityCell = cells.B2;
    const marketDateCell = cells.B3;
    marketDateCell.value = moment(backsolveDateCell.value as string, 'MM/DD/YYYY')
      .add(Number(maturityCell.value), 'years')
      .format('MM/DD/YYYY');
  }
};

const reverseParser = ({
  cells,
  columns,
  rowConfig,
  allowEmptyValues,
  fieldAttributes,
  tableData,
}: ReverseParserProps) => {
  const alphabet = alphabetGenerator([], columns.length);
  const { gpcApproaches } = tableData;
  columns.forEach((column: any, columnIndex: number) => {
    const columnLegend = alphabet[columnIndex];

    // copy original values
    const tmpColumn = column;

    rowConfig.forEach((_, rowIndex) => {
      const key = columnLegend + (rowIndex + 1);
      const cell = cells[key];
      const defaultValue = null;
      const type = cell.dbType ?? null;
      const format = cell.format ?? null;
      const value = cell ? cell.value : defaultValue;
      const { gridType } = cell;

      if (cell?.alias) {
        if (cell.alias === VOLATILITY_SOURCE_ALIAS) {
          updateApproachReference({
            approaches: gpcApproaches!,
            column: tmpColumn,
            lookup: (cell.value ?? '') as string | number,
            specificApproachKey: APPROACH_GPC_PROPERTY,
            updateKey: VOLATILITY_SOURCE_ALIAS,
            referenceKey: VOLATILITY_SOURCE_REFERENCE,
            extraOptions: [],
          });
        } else {
          const attributeDecimalPlaces = fieldAttributes[cell.alias]?.decimal_places;
          const decimalPlaces = typeof attributeDecimalPlaces === 'number'
            ? attributeDecimalPlaces
            : cell.dbDecimalPlaces;

          const calcValue = parseDatabaseValue({
            type,
            value,
            format,
            allowEmptyValues,
            gridType,
            decimalPlaces,
          });

          handleDateCells(cell, cells);

          tmpColumn[cell.alias] =
            cell.alias === VOLATILITY_SOURCE_ALIAS ? handleVolatilitySource({ cell, calcValue }) : calcValue;
        }
      }
    });
  });
};

export default reverseParser;
